import React from 'react'
import styled, { css } from 'styled-components'
import { inheritPrimaryColor } from '../colors'
import { useScrollReset } from '../scroll'
import { sort, assoc, intersection } from 'ramda'
import VState from '../v-state'
import { Link } from 'react-router-dom'

const intersectAll = (...arrays) => arrays.reduce(intersection)

const preloadedData = typeof window !== 'undefined' && window.__data__ && window.__data__.articles
const preloadedTags = typeof window !== 'undefined' && window.__data__ && window.__data__.tags
const ArticleSummary = styled(({ date, slug, tags, articleTags, title, maxHeight = '100000px', ...props }) => {
  const currentTags = tags.filter(({ id }) => typeof articleTags.find(x => x === id) !== 'undefined')
  return (
    <div {...props}>
        <div css={`max-height: ${maxHeight}; position: relative; overflow: hidden; font-size: 1.15em; line-height: 1.7em; color: rgb(100, 100, 100);`}>
          <Link css={`color: ${inheritPrimaryColor}; text-decoration: none; display: block;`} to={`/articles/${slug}`}>
            <h1 css={`display: inline-block; font-size: 1.4em; font-weight: 400; color: rgb(100, 100, 100); margin-bottom: 0px; &:hover { text-decoration: underline }`}>{title}</h1>
          </Link>
          {currentTags.map(({ id, name }) => <MiniTag key={id} children={name} />)}
          <DateTag>{date.replace('T', ' ')}</DateTag>
        </div>
    </div>
  )
})`
  flex-grow: 1;
  max-width: 800px;
  display: flex;
  flex-direction: column;
`
const MiniTag = styled.div`
  background-color: ${inheritPrimaryColor};
  color: white;
  font-size: 0.75em;
  white-space: nowrap;
  height: 18px;
  padding: 0px 7px;
  border-radius: 9px;
  display: inline-flex;
  margin-right: 5px;
  align-items: center;
`
const DateTag = styled.div`
  color: rgb(170, 170, 170);
  font-size: 0.9em;
  margin-bottom: 1.2em;
`
const ArticleSummaries = styled(({ data, ...props }) => {
  useScrollReset()
  const articles = data ? data.articles[1] : preloadedData[1]
  const tags = data ? data.tags : preloadedTags
  const sortedTags = sort((a, b) => b.count - a.count, tags)
  const usedTagsObj = {}
  for (let article of articles) {
    const { tags } = article
    const stringTags = tags.map(tag => `${tag}`)
    for (let tag of stringTags) {
      usedTagsObj[tag] = true
    }
  }
  const usedTags = sortedTags.filter(({ id }) => usedTagsObj.hasOwnProperty(id))
  const activeTags = activeTagsState.use()
  const activeAll = activeAllState.use()
  const activeTagsArr = []
  for (let id in activeTags) {
    if (activeTags[id]) activeTagsArr.push(id)
  }
  const filteredArticles = articles.filter(({ tags }) => {
    const stringTags = tags.map(tag => `${tag}`)
    if (activeAll) return true
    else if (intersectAll(stringTags, activeTagsArr).length === activeTagsArr.length) return true
    else return false
  })
  return (
    <div {...props}>
      <ContentWrapper>
        <TagsSection>
          <TagsHeader children='Tags' />
          <Tag
            name='All'
            active={activeAll}
            pointer={!activeAll}
            onClick={() => {
              if (!activeAll) {
                activeAllState.set(true)
                activeTagsState.reset()
              }
            }}
          />
          <div css={css`height: 1px; background-image: linear-gradient(to right, white, ${inheritPrimaryColor}, white)`} />
          {usedTags.map(({ name, id }) =>
            <Tag
              id={id}
              name={name}
              active={!!activeTags[id]}
              pointer
              onClick={() => {
                activeTagsState.set(state => assoc(id, !state[id], state))
                const activeTags = activeTagsState.get()
                if (Object.values(activeTags).filter(Boolean).length === 0) activeAllState.set(true)
                else activeAllState.set(false)
              }}
            />
          )}
        </TagsSection>
        <ContentSection>
          {filteredArticles.map(({ slug, title: { rendered }, date, tags: articleTags }) => <ArticleSummary tags={tags} articleTags={articleTags} slug={slug} key={slug} date={date} title={rendered} maxHeight='250px' />)}
        </ContentSection>
      </ContentWrapper>
    </div>
  )
})`
  margin-top: 30px;
  display: flex;
  justify-content: center;
  padding-bottom: 60px;
`
const activeTagsState = new VState({})
const activeAllState = new VState(true)
const Tag = styled(({ name, id, ...props }) => <div {...props}>{name}</div>)`
  margin: 10px;
  height: 34px;
  display: inline-flex;
  white-space: nowrap;
  color: ${inheritPrimaryColor};
  ${p => p.pointer ? 'cursor: pointer;' : 'cursor: default;'}
  user-select: none;
  align-items: center;
  padding: 0px 15px;
  border-radius: 17px;
  /* font-weight: bold; */
  font-size: 1.1em;
  ${p => p.active ? css`background-color: ${inheritPrimaryColor}; color: white;` : ''}
`
const TagsHeader = styled.h1`
  color: ${inheritPrimaryColor};
  margin-left: 10px;
  margin-top: 0px;
`
const TagsSection = styled.div`
  flex-grow: 1;
  flex-basis: 0px;
  padding-left: 10px;
  margin-right: 20px;
`
const ContentSection = styled.div`
  flex-grow: 1000000000000;
  flex-basis: 0px;
  max-width: 800px;
  margin-left: 20px;
`
const ContentWrapper = styled.div`
  flex-grow: 1;
  max-width: 1140px;
  display: flex;
  flex-wrap; wrap;
`

export default ArticleSummaries